import {
  Component,
  Host,
  Prop,
  State,
  Element,
  h,
  Watch,
  getAssetPath
} from "@stencil/core";

import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../common/locale";
import { config } from "../../common/config";

import { ActionListModel } from "@genexus/chameleon-controls-library";
import {
  ActionListItemModel,
  ActionListItemModelExtended,
  ActionListItemSeparator
} from "@genexus/chameleon-controls-library/dist/types/components/action-list/types";

const FILTER_ICON = getIconPath({
  category: "system",
  name: "filters",
  colorType: "on-surface"
});

// plugin icon if no iconUrl was provided
const EXTENSION_ICON_IMG = getAssetPath(
  `./gx-ide-assets/plugin-manager/images/plugin-icon.svg`
);

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/list-box",
  "components/button",
  "components/edit",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-plugin-explorer",
  styleUrl: "plugin-explorer.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/plugin-explorer"]
})
export class GxIdePluginManager {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #animateEmptyState: boolean = false;

  @Element() el: HTMLGxIdePluginExplorerElement;

  /**
   * If true it will hide the content, and display a loader
   */
  @State() loading: boolean = true;

  /**
   * The plugins model as ActionListModel, which is the model the ch-action-list expects
   */
  @State() pluginsActionListModel: ActionListModel = [];

  /**
   * List of plugins to display. Usually used to initialize the component.
   */
  @Prop({ mutable: true }) plugins: PluginData[];
  @Watch("plugins")
  pluginsChangedHandler(newPlugins: PluginData[]) {
    this.pluginsActionListModel =
      this.#mapPluginDataToActionListModel(newPlugins);
    this.loading = false;
  }

  /**
   * Callback that should be invoked when the user updates the filter.
   * A debounce should be applied before invoking this function.
   */
  @Prop() readonly filterCallback!: (text: string) => Promise<PluginData[]>;

  /**
   * Callback that should be invoked when the user clicks or presses Enter
   * on a selected plugin item.
   */
  @Prop() readonly showDetailsCallback!: (pluginId: string) => Promise<void>;

  /**
   * Callback that should be invoked when the user clicks on a plugin's action button.
   */
  @Prop() readonly executeActionCallback!: (
    pluginId: string,
    action: PluginAction
  ) => Promise<void>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.pluginsChangedHandler(this.plugins);
  }

  #actionListButtonClickHandler =
    (pluginId: string, action: PluginAction) => (event: MouseEvent) => {
      event.stopPropagation();
      this.executeActionCallback(pluginId, action);
    };

  #mapPluginDataToActionListModel = (
    pluginsData: PluginData[]
  ): ActionListModel => {
    const pluginsActionListModel: ActionListModel = [];
    const separatorItem: ActionListItemSeparator = { type: "separator" };

    pluginsData?.forEach((pluginData, index) => {
      const hasActionButton = !!pluginData.action;
      const pluginItem = {
        id: pluginData.id,
        caption: pluginData.name,
        imgSrc: pluginData.iconUrl,
        type: "actionable",

        additionalInformation: {
          "stretch-start": {
            center: [
              {
                part: "custom-image",
                jsx: () => (
                  <img
                    part="custom-image"
                    src={pluginData.iconUrl || EXTENSION_ICON_IMG}
                    alt={`${this.#componentLocale.logotype.replace(
                      "PLUGIN_NAME_PLACEHOLDER",
                      pluginData.name
                    )}`}
                    loading="lazy"
                  />
                )
              }
            ]
          },
          "block-end": {
            start: [
              {
                part: "custom-description custom-button custom-container custom-brand custom-brand-icon custom-image item__block-end start",
                jsx: () => (
                  <div part="custom-container" title={pluginData.description}>
                    <p part="custom-description">{pluginData.description}</p>
                    <small part="custom-brand">{pluginData.publisher}</small>
                  </div>
                )
              }
              // {
              //   slot: "abcd"
              // } as ActionListItemAdditionalSlot // slot not working
            ]
          },
          "stretch-end": hasActionButton && {
            center: [
              {
                jsx: () => (
                  <button
                    part="custom-button"
                    onClick={this.#actionListButtonClickHandler(
                      pluginData.id,
                      pluginData.action
                    )}
                  >
                    {pluginData.action}
                  </button>
                )
              }
            ]
          }
        }
      } as ActionListItemModel;
      if (index !== 0) {
        pluginsActionListModel.push(separatorItem);
      }
      pluginsActionListModel.push(pluginItem);
    });

    return pluginsActionListModel;
  };

  #pluginsFilterInputHandler = async (
    event: CustomEvent<string> | InputEvent
  ) => {
    this.loading = true;
    const filterValue = event.detail as string;
    this.plugins = await this.filterCallback(filterValue);
  };

  #evaluateContentToDisplay = () => {
    const pluginsToDisplay = this.pluginsActionListModel.length;
    return pluginsToDisplay ? (
      <ch-action-list-render
        model={this.pluginsActionListModel}
        class="list-box"
        selection="single"
        onSelectedItemsChange={this.#pluginSelectedHandler}
      >
        {/* <p slot="abcd">Slot for "abcd" id</p> */}
      </ch-action-list-render>
    ) : (
      <gx-ide-empty-state
        isAnimated={this.#animateEmptyState}
        class="recent-objects__empty-state"
        stateIconSrc={FILTER_ICON}
        stateTitle={this.#componentLocale.emptyStateTitle}
        stateDescription={this.#componentLocale.emptyStateDescription}
      ></gx-ide-empty-state>
    );
  };

  #renderLoader = () => {
    this.#animateEmptyState = true;
    return (
      <gx-ide-loader
        loaderTitle={this.#componentLocale.loaderTitle}
        description={this.#componentLocale.loaderDescription}
        show
      ></gx-ide-loader>
    );
  };

  #pluginSelectedHandler = (
    event: CustomEvent<ActionListItemModelExtended[]>
  ) => {
    const eventType = event.detail[0]?.item.type;
    if (eventType === "actionable") {
      const pluginId = event.detail[0].item.id;
      this.showDetailsCallback(pluginId);
    }
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <div class="outer-container field-group">
          <ch-edit
            id="search-plugins-input"
            class="input"
            accessibleName={this.#componentLocale.accessibleName}
            placeholder={this.#componentLocale.searchInputPlaceholder}
            debounce={config.inputDebounce}
            onInput={this.#pluginsFilterInputHandler}
            type="search"
          ></ch-edit>
          <div class="inner-container">
            {this.loading
              ? this.#renderLoader()
              : this.#evaluateContentToDisplay()}
          </div>
        </div>
      </Host>
    );
  }
}

export type PluginData = {
  id: string;
  iconUrl: string;
  name: string;
  description: string;
  publisher: string;
  action?: PluginAction;
};

export type PluginAction = "install" | "update";
